/*
interacttions are joined to Intervals they are in by interaction_node_interval in db
intervals have an interval_set_id column which places them into logical interval sets
there is a standalone interval_set table which contains metadata/description about each interval_set

In the UI, we use the interaction node structure to determine if an interval set has child interval sets
interval sets are NOT actually in a tree structure and do not have children
But their corresponding interactions do so we render the view like a tree to emulate the structure of the interaction node tree

the interaction set UI shows interaction set DB data, but only that which is also within the scope of selected pathway interaction
so there sometimes might be interactions within that interval set but NOT on the selected pathway - those would not be shown
*/

import { DatabaseTable, returnID } from "../BaseTable";
import Interval from "./Interval";
import { types } from "../../helpers/Util";
import store from "../../store/Store";
import { List } from "immutable";
import InteractionNodeInterval from "../interactions/InteractionNodeInterval";
import { deleteTableRow, postgrest_operators, runDatabaseFunction } from "../../helpers/HttpHelper";
import { interactionTreeSliceActions } from "../../store/slices/InteractionTreeSlice";
import InteractionNode from "../interactions/InteractionNode";
import InteractionSettings from "../config/InteractionSettings";
import Interaction from "../interactions/Interaction";

class IntervalSetRaw extends DatabaseTable {
  id: number;
  name: string | null;
  shared: boolean;
  modified: Date;

  static readonly serializableFields = new Map([
    ["id", [types.number]],
    ["name", [types.string, types.null]],
    ["shared", [types.boolean]],
    ["modified", [types.date, types.string]],
  ]);
  static readonly tableName = "IntervalSet";

  constructor(id = -1, name = null, shared = false, modified = new Date()) {
    super();
    this.id = returnID(id);
    this.name = name;
    this.shared = shared;
    this.modified = modified;
  }
}

class IntervalSet extends IntervalSetRaw {
  currentParent: number | null = null; // needed for "new interval set" placeholders, so we know where any new interactions on this set should go in the tree
  currentSet: number | null = null; // needed for "new interval set" placeholders, so we know where any new interactions on this set should go in the tree
  intervals: List<number> = List();

  constructor(...args: any[]) {
    super(...args);
  }

  protected initialize() {
    const intervals = store.getState().tables.tables.get(Interval) as
      | undefined
      | Map<number, Interval>;
    if (typeof intervals !== "undefined") {
      const myIntervals: number[] = [];
      intervals?.forEach((interval) => {
        if (interval.interval_set_id === this.id) {
          myIntervals.push(interval.id);
        }
      });
      this.intervals = List(myIntervals);
    }
    return super.initialize();
  }

  // when saving an intervalSet, we want to save all associated interaction_node_intervals
  async afterSave() {
    const allIntervals = store.getState().tables.tables.get(Interval) as
      | undefined
      | Array<Interval>;
    const allInteractionNodeIntervals = store
      .getState()
      .tables.tables.get(InteractionNodeInterval) as
      | undefined
      | Array<InteractionNodeInterval>;
    const allInteractionNodeIntervalsToDelete =
      store.getState().interactionTree.InteractionNodeIntervalsToDelete;
    console.log(allInteractionNodeIntervalsToDelete);

    const intervalsForThisIntervalSet = allIntervals?.filter(
      (interval: Interval) => {
        return interval.interval_set_id == this.id;
      },
    );

    console.log(intervalsForThisIntervalSet);
    const interactionNodeIntervalsForThisIntervalSet =
      allInteractionNodeIntervals?.filter((ini: InteractionNodeInterval) => {
        if (intervalsForThisIntervalSet) {
          return (
            Array.from(intervalsForThisIntervalSet.values()).findIndex(
              (interval) => ini.interval_id == interval.id,
            ) !== -1
          );
        }
        return false;
      });

    // make sure that the interaciton_node_intervals we commit for deletion are ONLY ones associated with this here interval_set
    const interactionNodeIntervalsToDeleteForThisIntervalSet =
      allInteractionNodeIntervalsToDelete?.filter(
        (ini: InteractionNodeInterval) => {
          if (intervalsForThisIntervalSet) {
            return (
              Array.from(intervalsForThisIntervalSet.values()).findIndex(
                (interval) => ini.interval_id == interval.id,
              ) !== -1
            );
          }
          return false;
        },
      );

    // save each interaction_node_interval associated with this intervalSet
    interactionNodeIntervalsForThisIntervalSet?.forEach(
      async (ini: InteractionNodeInterval) => {
        await ini.save();
      },
    );

    // delete each interaction_node_interval associated with this intervalSet which is designated for deletion
    interactionNodeIntervalsToDeleteForThisIntervalSet?.forEach(
      async (ini: InteractionNodeInterval) => {
        deleteTableRow("interaction_node_interval", "" + ini.id);
      },
    );

    // get an updated list of InteractionNodeIntervalsToDelete that doesnt still contain the ones we just deleted
    const idsToDelete = new Set(
      interactionNodeIntervalsToDeleteForThisIntervalSet.map((item) => item.id),
    );
    const updatedInteractionNodeIntervalsAfterDelete =
      allInteractionNodeIntervalsToDelete.filter(
        (item) => !idsToDelete.has(item.id),
      );

    // remove the deleted interaction_node_intervals from the InteractionNodeIntervalsToDelete redux array
    // so the system doesnt accidentally try to delete them again
    store.dispatch(
      interactionTreeSliceActions.setInteractionNodeIntervalsToDelete({
        append: false,
        InteractionNodeIntervalsToDelete:
          updatedInteractionNodeIntervalsAfterDelete,
      }),
    );
  }

  public static async addExisting(
    intervalSetID: number,
    setID: number,
    parentID: number,
    ordinal?: number,
  ) {
    //const submittedOrdinal = ordinal ? ordinal : 1;
    const submittedOrdinal = !ordinal || ordinal === 0 ? 1 : ordinal + 1 ;

    // since this is existing, dont add a new interval set
    // instead query for this intervalSet, add to redux
    IntervalSet.queryTable([
      {
        column: "id",
        operator: postgrest_operators.eq,
        value: intervalSetID.toString(),
      },
    ]);

    const properties = new InteractionSettings("interval_set");
    properties.intervalSetID = intervalSetID;
    
    // create new interaction in db and redux for intervalSet
    const newInteraction = await Interaction.createInstance({
      title: "Enter New Title",
      properties: properties,
    }).save();

    // create new interaction node in db and redux for intervalSet
    let newInteractionNode = await InteractionNode.createInstance({
      set_interaction_id : setID,
      parent_interaction_id : parentID,
      ordinal: submittedOrdinal,
      child_interaction_id: newInteraction.id,
    })
    
    newInteractionNode = await newInteractionNode.save();

    const params = {
      nodeid: newInteractionNode.id,
      parentid: parentID,
      old_ordinal: submittedOrdinal,
      new_ordinal: submittedOrdinal
    };

    const dbFunc = "update_ordinality_by_parent";
    try {
      await runDatabaseFunction(dbFunc, params)
    } catch (err: any) {
      console.error("Error with update_ordinality_by_parent: " + err);
    }

    InteractionNode.updateInteractionNodeOrdinalityByParentAndNode(
      newInteractionNode.id,
      newInteractionNode.parent_interaction_id,
      submittedOrdinal,
      submittedOrdinal, 
      false
    )
  }

  public async copy(){
    let params = {};
    const dbFunc = "fn_copy_interval_set";
    params = {
      intervalsetid: this.id,
      copied_ids: {}
    };

    try {
      const response = await runDatabaseFunction(dbFunc, params);
      const dataText = await response.text();
      const data = JSON.parse(dataText);
      console.log(data);
  
      return data; // Return the parsed data to the caller
    } catch (err: any) {
      console.error("Error with copying interaction: " + err);
    }
  }

}

export default IntervalSet;
